// tagValidation.js

export function isIntroTag(tag) {
  return typeof tag, 'string' && tag.trim().toLowerCase() === 'intro';
}

/** Clip tags only — used for duplicate rules on the Videos tab */
function getClipTagsInSection(selectedData, sectionKey) {
  const tags = [];
  const clips = selectedData?.[sectionKey]?.clips || [];
  clips.forEach(charClip => {
    (charClip.videos || []).forEach(video => {
      (video.vidclips || []).forEach(clip => {
        if (clip.tag?.trim()) tags.push(clip.tag.trim());
      });
    });
  });
  return tags;
}

function getSegmentTagsFromSections(sections) {
  const found = [];
  (sections || []).forEach((section, sIdx) => {
    (section.segment || []).forEach((seg, segIdx) => {
      if (seg.tag?.trim()) {
        found.push({
          tag: seg.tag.trim(),
          section: section.section,
          sectionIndex: sIdx,
          segmentIndex: segIdx,
        });
      }
    });
  });
  return found;
}

/** Global intro count — clip tags only (Videos tab) */
export function countIntroTags({ sections, selectedData }) {
  let count = 0;

  getSegmentTagsFromSections(sections).forEach(item => {
    if (isIntroTag(item.tag)) count += 1;
  });

  ['intro', 'body', 'conclusion'].forEach(key => {
    getClipTagsInSection(selectedData, key).forEach(tag => {
      if (isIntroTag(tag)) count += 1;
    });
  });

  return count;
}

/** Validate CLIP tag (Videos tab) — duplicates enforced here only */
export function validateClipTag({
  tag,
  sectionKey,
  sections,
  selectedData,
  exclude = null,
}) {
  const trimmed = (tag || '').trim();
  if (!trimmed) return { ok: true };

  if (isIntroTag(trimmed)) {
    let introCount = 0;

    getSegmentTagsFromSections(sections).forEach(item => {
      if (isIntroTag(item.tag)) introCount += 1;
    });

    ['intro', 'body', 'conclusion'].forEach(key => {
      getClipTagsInSection(selectedData, key).forEach(t => {
        if (isIntroTag(t)) introCount += 1;
      });
    });

    if (exclude?.previousTag && isIntroTag(exclude.previousTag)) {
      introCount = Math.max(0, introCount - 1);
    }

    const isReplacingSameIntro =
      exclude?.previousTag && isIntroTag(exclude.previousTag) && isIntroTag(trimmed);

    if (introCount > 0 && !isReplacingSameIntro) {
      return {
        ok: false,
        message: 'Only one "intro" tag is allowed in the entire video (case insensitive).',
      };
    }
    return { ok: true };
  }

  // Non-intro: unique per section across characters (clips only)
  const clips = selectedData?.[sectionKey]?.clips || [];
  for (const charClip of clips) {
    for (const video of charClip.videos || []) {
      for (let i = 0; i < (video.vidclips || []).length; i += 1) {
        const clip = video.vidclips[i];
        const existing = (clip.tag || '').trim();
        if (!existing || isIntroTag(existing)) continue;

        const isSelf =
          exclude &&
          exclude.character === charClip.characterName &&
          exclude.url === video.url &&
          exclude.clipIndex === i;

        if (isSelf) continue;

        if (existing === trimmed) {
          return {
            ok: false,
            message: `Tag "${trimmed}" already exists on a clip in the "${sectionKey}" section. Tags must be unique per section (case sensitive).`,
          };
        }
      }
    }
  }

  return { ok: true };
}

/**
 * Validate TIMELINE segment tag — NO duplicate rules (except one intro segment globally).
 * Timeline consumes clip tags; multiple segments may share the same tag.
 */
export function validateSegmentTag({
  tag,
  sectionKey,
  sections,
  selectedIndex,
  segmentIndex,
}) {
  const trimmed = (tag || '').trim();
  if (!trimmed) return { ok: true };

  // Only rule for timeline: one intro segment in the whole video (teaser)
  if (isIntroTag(trimmed)) {
    const otherIntroSegments = getSegmentTagsFromSections(sections).filter(
      item =>
        isIntroTag(item.tag) &&
        !(item.section === sectionKey && item.segmentIndex === segmentIndex)
    );

    if (otherIntroSegments.length > 0) {
      return {
        ok: false,
        message: 'Only one timeline segment can be tagged "intro" (case insensitive).',
      };
    }
  }

  // Non-intro segment tags: always allowed (including duplicates across segments)
  return { ok: true };
}

/** Clip segments in timeline must have a tag before generate */
export function findUntaggedClipSegments(sections) {
  const problems = [];
  (sections || []).forEach(section => {
    (section.segment || []).forEach((seg, idx) => {
      const text = (seg.text || '').trim();
      const isClip = text.startsWith('[CLIP:') && text.endsWith(']');
      if (isClip && !(seg.tag || '').trim()) {
        problems.push({
          section: section.section,
          segmentIndex: idx,
          text,
        });
      }
    });
  });
  return problems;
}

export function hasIntroTagAnywhere({ sections, selectedData }) {
  const segIntro = getSegmentTagsFromSections(sections).some(item => isIntroTag(item.tag));
  const clipIntro = ['intro', 'body', 'conclusion'].some(key =>
    getClipTagsInSection(selectedData, key).some(t => isIntroTag(t))
  );
  return segIntro || clipIntro;
}